import React, { useEffect, useState } from 'react';
import {
    Tabs,
    Table,
    Button,
    Form,
    Input,
    Select,
    Modal,
    Tag,
    Space,
    Divider,
    Card,
    Typography,
    Upload,
    UploadFile,
    Spin,
    Popconfirm,
    message,
    Flex
} from 'antd';
import {
    PlusOutlined,
    SearchOutlined,
    EyeOutlined,
    CloseOutlined,
    CheckOutlined,
    UploadOutlined
} from '@ant-design/icons';
import { useStore } from '../../stores/root-store-context';
import { TicketChat } from './TicketChat';
import { AuthorType, getEnumName, NewTicket, Ticket, TicketStatus } from '../../api/TicketApi';
import { observer } from 'mobx-react-lite';
import dayjs, { Dayjs } from 'dayjs';
import { TicketDetails } from './TicketDetails';
import Layout from '../../components/layout/Layout';
import { useTranslation } from 'react-i18next';
import { MessageOutlined, CommentOutlined, WechatOutlined } from '@ant-design/icons';
import CreateTicketModal from './CreateTicketModal';
import { Trash2 } from 'lucide-react';
import { getUserComponent } from '../../helpers/user';
import { useNavigate, useParams } from 'react-router-dom';
// ==================== TYPES ====================


interface CreateTicketFormValues {
    category: string;
    subject: string;
    description: string;
    attachments: UploadFile[];
}

// ==================== COMPONENT ====================
const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

type RouteParam = {
    [key: string]: string | undefined
}

const TicketSupportSystem: React.FC = observer(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const params = useParams<RouteParam>();
    const ticketPublicId = params.publicId;


    const [activeTab, setActiveTab] = useState<string>('all');
    const { ticketStore, ticketChatStore, applicationStore, employeesStore } = useStore();
    const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);
    const [viewModalVisible, setViewModalVisible] = useState<boolean>(false);
    const [filteredTickets, setFilteredTickets] = useState<Ticket[]>([]);
    const [form] = Form.useForm<CreateTicketFormValues>();

    useEffect(() => {
        ticketStore.getMyTicketsActive(applicationStore.company.Tenant).then((tickets) => {
            const ticket = tickets?.find(ticket => ticket.publicId == ticketPublicId || ticket.id == ticketPublicId);
            if (ticket) {
                ticketStore.setCurrentTicket(ticket);
                setViewModalVisible(true);
            }   
        });
    }, [ticketPublicId]);

    const [fileList, setFileList] = useState<UploadFile[]>([]);

    const handleChange = ({ fileList }: { fileList: UploadFile[] }) => {
        setFileList(fileList);
    };

    useEffect(() => {
        ticketStore.getMyTicketsActive(applicationStore.company.Tenant).then((tickets) => {
            if (tickets) {
                setFilteredTickets(tickets);
            }
        });
    }, []);

    useEffect(() => {
        if (activeTab === 'closed') {
            ticketStore.getMyTicketsClosed(applicationStore.company.Tenant).then((tickets) => {
                console.log(tickets);
                if (tickets) {
                    setFilteredTickets(tickets);
                }
            });
        } else {
            const newFiltered: Ticket[] = ticketStore.activeTickets.filter(ticket => {
                if (activeTab === 'all') return true;
                if (activeTab === 'my') return ticket.email === applicationStore.user.email;
                if (activeTab === 'closed') return ticket.status === 2;
                return true;
            }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
            setFilteredTickets(newFiltered);
        }

    }, [activeTab, ticketStore.activeTickets, applicationStore.user.email]);

    const columns: any = [
        {
            title: t('TicketID'),
            dataIndex: 'publicId',
            key: 'publicId',
            sorter: (a: Ticket, b: Ticket) => a.publicId.localeCompare(b.publicId),
        },
        {
            title: t('Subject'),
            dataIndex: 'subject',
            key: 'subject',
            width: '300px',
            sorter: (a: Ticket, b: Ticket) => a.subject.localeCompare(b.subject),
        },
        {
            title: t('Category'),
            dataIndex: 'category',
            key: 'category',
            render: (status: string) => {
                return <Tag >{t('supportCategories.' + status)}</Tag>;
            },
            filters: [
                ...filteredTickets.map((ticket) => ({
                    text: t('supportCategories.' + ticket.category),
                    value: ticket.category,
                })).filter(
                    (ticket, index, self) =>
                        index === self.findIndex((t) => t.value === ticket.value)
                )
            ],
            onFilter: (value: string, record: Ticket) => record.category === value,
        },
        {
            title: t('Status'),
            dataIndex: 'status',
            key: 'status',
            render: (status: number) => {
                let color = '';
                switch (status) {
                    case 0:
                        color = 'blue';
                        break;
                    case 1:
                        color = 'orange';
                        break;
                    case 2:
                        color = 'green';
                        break;
                    case 3:
                        color = 'red';
                        break;
                }
                return <Tag color={color}>{t(getEnumName(TicketStatus, status))}</Tag>;
            },
            sorter: (a: Ticket, b: Ticket) => a.status - b.status,
        },
        {
            title: t('CreatedAt'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a: Ticket, b: Ticket) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
            render: (text: string, record: Ticket) => <div>
                <div>{getUserComponent(applicationStore.siteConfig.baseCloudinary, employeesStore.users?.find(user => user.email === record.email), applicationStore.company?.Tenant, 80)}</div>
                <div style={{ textAlign: 'center' }}>{dayjs(record.createdAt).format('DD MMMM YYYY HH:mm')}</div>
            </div>,
        },
        {
            title: t('Actions'),
            key: 'actions',
            width: '250px',
            render: (_: any, record: Ticket) => (
                <Space size="middle">
                    <Flex vertical>
                        <div style={{ width: '150px' }}>
                            📨 {t('Messages')}: <strong>{record.messages.filter(m => m.authorType === AuthorType.User).length}</strong>
                        </div>
                        <div style={{ width: '150px' }}>
                            📨 {t('Replies')}: <strong>{record.messages.filter(m => m.authorType === AuthorType.Support).length}</strong>
                        </div>
                    </Flex>
                    <Flex vertical gap={10}>

                        <Button
                            style={{ fontSize: '14px', padding: '4px 25px', height: '44px' }}
                            icon={<CommentOutlined style={{ fontSize: '44px' }} />}
                            onClick={() => handleViewTicket(record)}
                        >
                            {t('OpenChat')}
                        </Button>
                        {record.status !== TicketStatus.ResolvedCustomer && record.status !== TicketStatus.ResolvedSupport && (
                            <Popconfirm
                                title={t('ResolveTicket')}
                                description={t('ResolveTicketConfirmation')}
                                onConfirm={() => handleCloseTicket(record.id)}
                                okText={t('Yes')}
                                cancelText={t('No')}
                            >
                                <Button
                                    style={{ fontSize: '14px', padding: '4px 25px', height: '44px' }}
                                    icon={<CloseOutlined style={{ fontSize: '24px' }} />}
                                    danger
                                >
                                    {t('CloseTicket')}
                                </Button>
                            </Popconfirm>
                        )}

                    </Flex>
                </Space>
            ),
        },
    ];

    const handleTabChange = (key: string) => {
        setActiveTab(key);
    };

    const handleCreateTicket = () => {
        form.validateFields().then((values: CreateTicketFormValues) => {
            const newTicket: NewTicket = {
                companyId: applicationStore.company.Id || '0',
                email: applicationStore.user.email || '',
                userName: `${applicationStore.user.firstName} ${applicationStore.user.lastName}` || '',
                userRole: applicationStore.user.roles.map(role => role.name).join(',') || '',
                category: values.category,
                subject: values.subject,
                description: values.description,
                companyName: ticketStore.companyName,
                companySystemName: applicationStore.company?.Tenant || ''
            };

            const formDataMessage = new FormData();
            formDataMessage.append('text', values.description);
            formDataMessage.append('username', `${applicationStore.user.firstName} ${applicationStore.user.lastName}`);
            formDataMessage.append('email', applicationStore.user.email);
            formDataMessage.append('role', applicationStore.user.roles.map(role => role.name).join(',') || 'User');
            formDataMessage.append('authorType', AuthorType.User.toString());

            if (values.attachments && values.attachments.length > 0) {
                values.attachments.forEach((file, index) => {
                    if (file.originFileObj) {
                        formDataMessage.append('files', file.originFileObj);
                    }
                });
            } else {
                formDataMessage.append("files", "");
            }

            ticketStore.createNew(newTicket).then((result) => {
                let ticketId = result.value.id;
                console.log(result);
                ticketChatStore.addMessageFormData(ticketId, formDataMessage).then(() => {
                    message.success('Ticket created successfully');
                    ticketStore.getMyTicketsActive(applicationStore.company.Tenant).then((tickets) => {
                        if (tickets) {
                            setFilteredTickets(tickets);
                            navigate(`/support/${ticketId}`);
                        }
                    });
                });

            });

            setCreateModalVisible(false);
            form.resetFields();
        });
    };

    const handleViewTicket = (ticket: Ticket) => {
        setViewModalVisible(true);
        navigate(`/support/${ticket.id}`);
    };

    const handleCloseTicket = (ticketId: string) => {
        ticketStore.update(ticketId, { status: TicketStatus.ResolvedCustomer } as Ticket).then(() => {
            message.success('Ticket closed successfully');
            ticketStore.getMyTicketsActive(applicationStore.company.Tenant).then((tickets) => {
                if (tickets) {
                    setFilteredTickets(tickets);
                }
            });
        });
    };

    return (
        <Layout headerStyle={1} footerStyle={2} breadcrumbTitle={t("Support")}>
            <div style={{ padding: '24px' }}>
                <Card>
                    <Tabs
                        activeKey={activeTab}
                        onChange={handleTabChange}
                        tabBarExtraContent={
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => setCreateModalVisible(true)}
                            >
                                {t('Create')} {t('Ticket')}
                            </Button>
                        }
                    >
                        <TabPane tab={t('AllTickets')} key="all" />
                        <TabPane tab={t('MyTickets')} key="my" />
                        <TabPane tab={t('ViewClosedTickets')} key="closed" />
                    </Tabs>
                    <Spin spinning={ticketStore.isLoading}>
                        <Table
                            columns={columns}
                            dataSource={filteredTickets}
                            rowKey="id"
                            bordered
                            pagination={{ pageSize: 10 }}
                            scroll={{ x: 'max-content' }}
                        />
                    </Spin>

                </Card>
                <CreateTicketModal
                    visible={createModalVisible}
                    form={form}
                    fileList={fileList}
                    onCancel={() => {
                        setCreateModalVisible(false);
                        form.resetFields();
                    }}
                    onSubmit={handleCreateTicket}
                    onFileChange={handleChange}
                />
                <TicketDetails
                    mode="customer"
                    visible={viewModalVisible}
                    ticket={ticketStore.currentTicket}
                    onClose={() => {
                        setViewModalVisible(false);
                        ticketChatStore.disconnect(ticketStore.currentTicket?.id || '');
                        navigate(`/support`);
                    }}
                    onCloseTicket={() => {

                    }}
                />

            </div>
        </Layout>

    );
});

export default TicketSupportSystem;